/**
 * Megamenu types matching i18n structure (megamenu.navigationItems).
 * Data lives in i18n/translations/{locale}/megamenu.ts.
 * Data is in i18n: use tm('megamenu.navigationItems') to read nav items.
 */

/** Same `{ label, href }` on nav items (`MegamenuNavItem`) and section rows (`MegamenuSection`). */
export interface MegamenuPrimaryLink {
  label: string
  href: string
}

export interface MegamenuLink {
  label: string
  href?: string
}

export interface MegamenuSubSection {
  title: string
  items: MegamenuLink[]
}

export interface MegamenuSection {
  title: string
  /** Section row that links to promotion search (special styling + GTM). */
  isPromotionSalesLink?: boolean
  /**
   * When set (without using this section for a link column), the sidebar row is
   * a single direct link — same `{ label, href }` as `MegamenuNavItem.primaryLink`.
   */
  primaryLink?: MegamenuPrimaryLink
  items?: MegamenuLink[]
  subSections?: MegamenuSubSection[]
}

export interface MegamenuNavItem {
  path: string
  label: string
  showOnSub?: boolean
  /** When true, sections are shown as accordion in place instead of opening a separate panel */
  expandInPanel?: boolean
  primaryLink?: MegamenuPrimaryLink
  sections?: MegamenuSection[]
  items?: MegamenuLink[]
}

export type MegamenuNavigationItems = MegamenuNavItem[]

/** Path for promotion deals row in Experiences dropdown. Use localePath() in components. */
export const megamenuSalesPath = '/search/?hasPromotion=true'

/** Sections listed in the megamenu sidebar (includes direct-link-only rows). */
export const megamenuSectionShowsInSidebar = (section: MegamenuSection) =>
  (section.items?.length ?? 0) > 0 ||
  (section.subSections?.length ?? 0) > 0 ||
  (section.isPromotionSalesLink === true &&
    (section.items?.length ?? 0) === 0) ||
  Boolean(section.primaryLink?.href)

/**
 * Resolves which section drives the desktop dropdown right panel for a given
 * sidebar selection index (skips rows that are sidebar-only links).
 */
export const resolveMegamenuDropdownPanelSection = (
  sections: MegamenuSection[],
  selectedIndex: number
): MegamenuSection | null => {
  if (!sections.length) return null
  const i = Math.min(Math.max(0, selectedIndex), sections.length - 1)
  const at = sections[i]
  const hasPanel = (s: MegamenuSection) =>
    !s.primaryLink?.href &&
    ((s.items?.length ?? 0) > 0 ||
      (s.subSections?.length ?? 0) > 0 ||
      (s.isPromotionSalesLink === true && (s.items?.length ?? 0) === 0))

  if (at && hasPanel(at)) return at
  for (let j = i - 1; j >= 0; j--) {
    const s = sections[j]
    if (s && hasPanel(s)) return s
  }
  for (let j = i + 1; j < sections.length; j++) {
    const s = sections[j]
    if (s && hasPanel(s)) return s
  }
  return null
}
